import {Plain} from '../../../../../../shared/model/plain.type.model';
import {LabelTranslator} from '../../../model/label-translator.model';
import {EmailBlock} from './email-block.model';
import {translateEmailBlock} from './translate-email-block.model';
import {EmailColumnOptions} from './email-column-options.model';
import {EMAIL_DEFAULT_BG_COLOR} from './email-default-bg-color.model';

/** Une colonne d'une section : ce qu'elle porte, et la part de largeur qu'elle occupe. */
export class EmailColumn {
    public content: EmailBlock[];
    public options: EmailColumnOptions;

    constructor(data: Plain<EmailColumn>) {
        // L'API omet les champs non renseignés : on normalise en structures vides, jamais en null.
        this.content = data.content ?? [];
        this.options = data.options ?? {width: 100, bgColor: EMAIL_DEFAULT_BG_COLOR};
    }

    public readonly withOptions = (patch: Partial<EmailColumnOptions>): EmailColumn =>
        new EmailColumn({...this, options: {...this.options, ...patch}});

    /** Insère un bloc au rang voulu ; un rang hors des bornes est ramené dedans. */
    public readonly withBlockAdded = (block: EmailBlock, index: number): EmailColumn => {
        const content = [...this.content];
        content.splice(this.boundedRank(index), 0, block);

        return new EmailColumn({...this, content});
    };

    public readonly withBlock = (index: number, block: EmailBlock): EmailColumn =>
        new EmailColumn({...this, content: this.content.map((current, rank) => (rank === index ? block : current))});

    public readonly withBlockRemoved = (index: number): EmailColumn =>
        new EmailColumn({...this, content: this.content.filter((_, rank) => rank !== index)});

    public readonly withBlockMoved = (from: number, to: number): EmailColumn => {
        if (!this.content[from]) return this;

        const content = [...this.content];
        const [moved] = content.splice(from, 1);
        content.splice(this.boundedRank(to), 0, moved);

        return new EmailColumn({...this, content});
    };

    /**
     * La colonne, ses libellés de gabarit traduits. Rendue à l'identique quand aucun de ses blocs
     * n'en portait : c'est à cela que l'appelant reconnaît qu'il n'y a rien à enregistrer.
     */
    public readonly withTranslatedTexts = (translate: LabelTranslator): EmailColumn => {
        const content = this.content.map((block) => translateEmailBlock(block, translate));
        if (content.every((block, rank) => block === this.content[rank])) return this;

        return new EmailColumn({...this, content});
    };

    /** Un dépôt en pleine colonne ne doit pas dépendre du pixel visé. */
    private readonly boundedRank = (index: number): number => Math.min(Math.max(index, 0), this.content.length);
}

/** Colonne vierge, prête à recevoir un bloc. */
export const buildEmailColumn = (width: number, bgColor: string): EmailColumn =>
    new EmailColumn({content: [], options: {width, bgColor}});
